import { AnswerOptionType, ModifiedAnswerOption } from '@/client/quizzes';
import { SecurityProfileQuiz } from '@/client/security-profile';
import { LoadingStatuses } from '@/common/constants';
import { SECURITY_PROFILE_TEXT_RESPONSE_MAX_LENGTH } from '@/common/constants/security-profile';
import { useSecurityProfileText } from '@/hooks/useSecurityProfileText';
import AppButton from '@/ui/buttons/AppButton';
import { AppIconTooltip } from '@/ui/icon-tooltip/AppIconTooltip';
import { ProgressHeader } from '@/ui/progress-header/ProgressHeader';
import { modifyCourseQuiz } from '@/utils/helpers/courses.helper';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledAnswerOption,
  StyledAnswerOptionContainer,
  StyledCustomInputTextarea,
  StyledMultiChoiceText,
  StyledOnboardingContainer,
  StyledOnboardingDescription,
  StyledOnboardingOverlay,
  StyledOnboardingTitle,
  StyledPill,
  StyledQuestionButtonArea,
  StyledQuestionCard,
  StyledQuestionHeader,
  StyledQuestionHeaderTitleBlock,
  StyledQuestionText,
  StyledWatermark,
} from './styles';

interface QuestionResponse {
  selectedAnswers: {
    id: string;
    isCorrect: boolean;
    type: AnswerOptionType;
  }[];
  textResponse?: string;
}

interface OnboardingQuestionProps {
  quiz: SecurityProfileQuiz;
  currentQuestionIndex: number;
  totalQuestions: number;
  responses: Record<string, QuestionResponse>;
  onOptionSelect: (
    answerOption: ModifiedAnswerOption,
    checked: boolean,
  ) => void;
  onTextChange: (value: string) => void;
  onNext: () => void;
  onSkip: () => void;
  isSubmitting: boolean;
  isLastQuestion: boolean;
  userName?: string;
  questionOrder: number[];
}

export const OnboardingQuestion: React.FC<OnboardingQuestionProps> = ({
  quiz,
  currentQuestionIndex,
  totalQuestions,
  responses,
  onOptionSelect,
  onTextChange,
  onNext,
  onSkip,
  isSubmitting,
  isLastQuestion,
  userName,
  questionOrder,
}) => {
  const { t } = useTranslation();
  const {
    getQuestionText,
    getAnswerOptionText,
    getAnswerOptionPlaceholder,
    getAnswerOptionTooltip,
  } = useSecurityProfileText();

  const adaptedQuiz = useMemo(() => {
    if (!quiz) return null;
    return modifyCourseQuiz(quiz);
  }, [quiz]);

  const reorderedQuestionIndex = questionOrder[currentQuestionIndex];
  const currentQuestion = adaptedQuiz?.questions?.[reorderedQuestionIndex];
  const currentQuestionId = currentQuestion?.id;
  const currentResponse = responses[currentQuestionId || ''] || {
    selectedAnswers: [],
    textResponse: undefined,
  };

  const isMultiChoice = currentQuestion?.type === 'multiple-choice';

  return (
    <StyledOnboardingOverlay>
      <StyledOnboardingContainer>
        <StyledQuestionHeader>
          <StyledPill>
            {userName
              ? t('securityProfile.onboarding.welcomeUser', {
                  name: userName,
                })
              : t('securityProfile.onboarding.welcomePill')}
          </StyledPill>
          <StyledQuestionHeaderTitleBlock>
            <StyledOnboardingTitle style={{ textAlign: 'center' }}>
              {t('securityProfile.onboarding.title')}
            </StyledOnboardingTitle>
            <StyledOnboardingDescription style={{ textAlign: 'center' }}>
              {t('securityProfile.onboarding.questionSubtitle')}
            </StyledOnboardingDescription>
          </StyledQuestionHeaderTitleBlock>
        </StyledQuestionHeader>

        <StyledQuestionCard>
          <ProgressHeader
            className="quiz-progress-header"
            currentIndex={currentQuestionIndex}
            totalCount={totalQuestions}
            translationKey="securityProfile.onboarding.questionProgress"
          />
          <StyledQuestionText isMultiChoice={isMultiChoice}>
            {getQuestionText(currentQuestion)}
          </StyledQuestionText>
          <StyledAnswerOptionContainer>
            {isMultiChoice && (
              <StyledMultiChoiceText
                dangerouslySetInnerHTML={{
                  __html: t('content.quiz.multiChoice.text'),
                }}
              />
            )}
            {currentQuestion?.answerOptions
              ?.filter((ao) => !ao.hidden)
              .map((answerOption: ModifiedAnswerOption) => {
                const isSelected = currentResponse.selectedAnswers.some(
                  (opt) => opt.id === answerOption.id,
                );
                const answerText = getAnswerOptionText(
                  currentQuestion,
                  answerOption,
                );
                const tooltipText = getAnswerOptionTooltip(
                  currentQuestion,
                  answerOption,
                );

                return (
                  <React.Fragment key={answerOption.id}>
                    <StyledAnswerOption $isSelected={isSelected}>
                      {isMultiChoice ? (
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) =>
                            onOptionSelect(answerOption, !!e.checked)
                          }
                          inputId={answerOption.id}
                        />
                      ) : (
                        <RadioButton
                          checked={isSelected}
                          onChange={(e) =>
                            onOptionSelect(answerOption, !!e.checked)
                          }
                          inputId={answerOption.id}
                        />
                      )}
                      <span>{answerText}</span>
                      {tooltipText && (
                        <AppIconTooltip
                          target={`tooltip-${answerOption.id}`}
                          content={tooltipText}
                          position="top"
                          baseZIndex={10001}
                        />
                      )}
                    </StyledAnswerOption>
                    {answerOption.type === AnswerOptionType.CUSTOM_INPUT &&
                      isSelected && (
                        <StyledCustomInputTextarea
                          value={currentResponse.textResponse || ''}
                          onChange={(e) => onTextChange(e.target.value)}
                          maxLength={SECURITY_PROFILE_TEXT_RESPONSE_MAX_LENGTH}
                          placeholder={getAnswerOptionPlaceholder(
                            currentQuestion,
                            answerOption,
                          )}
                        />
                      )}
                  </React.Fragment>
                );
              })}
          </StyledAnswerOptionContainer>
        </StyledQuestionCard>

        <StyledQuestionButtonArea>
          <AppButton
            severity="success"
            onClick={onNext}
            isDisabled={
              currentResponse.selectedAnswers.length === 0 || isSubmitting
            }
            state={
              isSubmitting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
            }
            label={
              isLastQuestion
                ? t('securityProfile.onboarding.finishProfile')
                : t('securityProfile.onboarding.nextQuestion')
            }
          />
          <AppButton
            severity="underline"
            onClick={onSkip}
            label={t('securityProfile.onboarding.skipForNow')}
          />
        </StyledQuestionButtonArea>

        <StyledWatermark aria-hidden="true">CyberPilot</StyledWatermark>
      </StyledOnboardingContainer>
    </StyledOnboardingOverlay>
  );
};
